import { useLocation, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { Box, Typography, Grid, Divider, Button } from '@mui/material';
import { styles } from '~/modules/auth/styles.ts';
import { FormProvider } from 'react-hook-form';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { ResetPasswordModal } from '~/modules/auth/sign-in/components/ResetPasswordModal.tsx';
import { errorMessagesSignIn } from '~/constants/errorMessages.ts';
import { TSignInForm } from '~/modules/auth/types.ts';
import { useSingInForm } from '~/modules/auth/hooks/useHookForms.ts';
import { FormPassword } from '~/components/FormComponents/FormPassword.tsx';
import { useAppDispatch } from '~/store/appDispatch';
import { login, loginVerify, resetPassword } from '~/store/users/thunks';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import {
  clearRedirectPath,
  getRedirectPath,
  resolveLocationRedirect,
  syncLocationRedirectToStorage,
} from '~/modules/auth/authRedirect.ts';
import OTPCodeModal from '~/components/OTPCodeModal/OTPCodeModal.tsx';
import { selectUsers } from '~/store/identification-form/selectors.ts';

export default function SignIn() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: unknown } | null;
  const from = locationState?.from ?? '/services';
  const locationRedirect = resolveLocationRedirect(locationState?.from);

  const [isModalOpenResetPassword, setIsModalOpenResetPassword] = useState(false);
  const [modalOTPVerification, setModalOTPVerification] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  const { token, mfaToken } = useSelector(selectUsers) || {};

  const form = useSingInForm();
  const { isSubmitting } = form.formState;
  const usernameField = form.watch('username');

  const onSubmit = async (values: TSignInForm) => {
    try {
      const loginResponse = await dispatch(login(values)).unwrap();
      setPhone(loginResponse?.phone);
      setModalOTPVerification(true);
    } catch (error) {
      form.setError('password', { message: errorMessagesSignIn.LOGIN_FAILED });
    }
  };

  const closeModalOpenResetPassword = () => setIsModalOpenResetPassword(false);
  const navigateToSignUp = () => navigate('/sign-up');

  const handleResetPassword = async () => {
    if (!usernameField) {
      form.setError('username', { message: errorMessagesSignIn.LOGIN_FAILED_REQUIRED });
      return;
    }

    try {
      await dispatch(resetPassword({ username: usernameField }));
      setIsModalOpenResetPassword(true);
    } catch (error) {
      form.setError('password', { message: errorMessagesSignIn.LOGIN_FAILED });
    }
  };

  const handleValidationSuccess = async (verificationToken?: string) => {
    setModalOTPVerification(false);
    try {
      await dispatch(loginVerify({ mfaToken, verificationToken: verificationToken! })).unwrap();
      navigate('/services');
    } catch {
      form.setError('password', { message: errorMessagesSignIn.LOGIN_FAILED });
    }
  };

  const closeOtpModal = () => {
    setModalOTPVerification(false);
    setOtpError('');
  };

  useEffect(() => {
    syncLocationRedirectToStorage(locationState?.from);
  }, [locationState, locationRedirect]);

  useEffect(() => {
    if (token) {
      const redirectPath = getRedirectPath();

      navigate(
        redirectPath || locationRedirect || (typeof from === 'string' ? from : '/services'),
        {
          replace: true,
        }
      );

      if (redirectPath) {
        clearRedirectPath();
      }
    }
  }, [from, locationRedirect, navigate, token]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.containerSignIn}>
        <Typography sx={styles.headerText}>Вхід</Typography>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid>
              <FormGridItem>
                <FormInput name="username" label="Логін" placeholder="Введіть логін" fullWidth />
              </FormGridItem>
              <FormGridItem>
                <FormPassword
                  type="password"
                  autoComplete="current-password"
                  name="password"
                  label="Пароль"
                  placeholder="Введіть пароль"
                  fullWidth
                />
              </FormGridItem>
            </Grid>
            <Typography variant={'body2'} sx={styles.textLink}>
              <Box component="a" sx={{ cursor: 'pointer' }} onClick={handleResetPassword}>
                Забули пароль?
              </Box>
            </Typography>

            <ResetPasswordModal
              isOpened={isModalOpenResetPassword}
              closeModal={closeModalOpenResetPassword}
            />

            <Grid sx={styles.buttonGroup}>
              <Button type="submit" variant={'contained'} fullWidth disabled={isSubmitting}>
                Увійти
              </Button>
              <Box sx={styles.dividerContainer}>
                <Divider sx={styles.divider} />
                <Typography sx={styles.dividerContainerText}>або</Typography>
                <Divider sx={styles.divider} />
              </Box>
              <Button type="submit" variant={'outlined'} fullWidth onClick={navigateToSignUp}>
                Зареєструватись
              </Button>
            </Grid>
          </form>

          <OTPCodeModal
            MFAFlow
            modalOTPVerification={modalOTPVerification}
            phone={phone!}
            error={otpError}
            setError={setOtpError}
            onClose={closeOtpModal}
            onValidationSuccess={handleValidationSuccess}
          />
        </FormProvider>
      </Box>
    </Box>
  );
}
